import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import { AutoComplete as AntdAutoComplete } from "antd";
import { QuestionType } from "@/app/types/typeConfig/chatQuestion";
import { debounce } from "@/app/utils/index";
import {
  FuzzyMatchingProblemProps,
  handleFuzzyMatchingProblem,
} from "@/app/api/chatContent";

import { useCurrentLocale } from "@/app/lib/LocaleProvider";
import { ImServiceChannel } from "@/app/store/types";

/** 弹层挂载根标记：getPopupContainer 用 closest 固定父节点，避免 portal 在 body 与本地容器间切换引发 DOM 不同步 */
const POPUP_ROOT_ATTR = "data-autocomplete-popup-root";

interface Props {
  value: string;
  onChange?: (value: string) => void;
  onSelect?: (value: string, option: any) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onPaste?: (
    e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onFocus?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  placeholder?: string;
  className?: string;
  maxLength?: number;
  inputType?: "textarea" | "input"; // 控制渲染textarea还是input
  inputRef?: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  popupClassName?: string; // 自定义弹出框样式类名
  classNames?: any; // 支持 antd v5 的 classNames prop, e.g. { popup: { root: '...' } }
  /** 与 store 一致：仅机器人链路下调用联想接口（与 socket 连接态解耦） */
  imServiceChannel?: ImServiceChannel;
  disabled?: boolean; // 是否禁用输入框
}

const AutoComplete: React.FC<Props> = ({
  value,
  onChange,
  onSelect,
  onKeyDown,
  onPaste,
  onFocus,
  maxLength,
  placeholder,
  className,
  inputType = "textarea", // 默认textarea
  inputRef,
  popupClassName,
  classNames,
  imServiceChannel = ImServiceChannel.Robot,
  disabled = false,
}) => {
  const [options, setOptions] = useState<QuestionType[]>([]);
  const [mounted, setMounted] = useState(false);
  const internalRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(
    null,
  );

  const getPopupContainer = useCallback((triggerNode: HTMLElement) => {
    // 保护1：节点为空
    if (!triggerNode) return document.body;

    // 保护2：节点已从 DOM 卸载
    if (!document.contains(triggerNode)) return document.body;

    // 向上查找稳定的祖先容器
    const root = triggerNode.closest(
      `[${POPUP_ROOT_ATTR}]`,
    ) as HTMLElement | null;

    return root ?? document.body;
  }, []);

  const applyMaxLengthToElement = (
    el: HTMLInputElement | HTMLTextAreaElement | null,
  ) => {
    if (!el) return;
    if (typeof maxLength === "number") {
      try {
        el.maxLength = maxLength;
        el.setAttribute("maxlength", String(maxLength));
      } catch {
        // ignore
      }
    } else {
      try {
        el.removeAttribute("maxlength");
      } catch {
        // ignore
      }
    }
  };

  const setInputRefs = useCallback(
    (node: HTMLInputElement | HTMLTextAreaElement | null) => {
      internalRef.current = node;

      // 在 ref 赋值时直接设置 maxLength
      if (node && typeof maxLength === "number") {
        node.setAttribute("maxlength", String(maxLength));
      }

      if (inputRef) {
        (inputRef as React.MutableRefObject<any>).current = node;
      }
    },
    [maxLength, inputRef],
  );

  const locale = useCurrentLocale();

  // 避免 hydration 错误：等待客户端挂载后再使用动态 locale
  useEffect(() => {
    setMounted(true);
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (val: string) => {
        if (!val) {
          setOptions([]);
          return;
        }
        // 仅机器人接待链路调用联想；人工会话为 ImServiceChannel.Human
        if (imServiceChannel !== ImServiceChannel.Robot) {
          setOptions([]);
          return;
        }

        const params: FuzzyMatchingProblemProps = {
          content: val,
          language: locale,
        };
        try {
          const res = await handleFuzzyMatchingProblem(params);
          setOptions(res || []);
        } catch (err) {
          console.error("fuzzy search failed", err);
          setOptions([]);
        }
      }, 800),
    [locale, imServiceChannel],
  );

  useEffect(() => {
    if (!value?.trim()) {
      debouncedSearch("");
      setOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleSearch = (val: string) => debouncedSearch(val);

  const handleSelect = (val: string, option: any) => {
    // 选中后立即清空 options，防止下次点击时显示旧的搜索结果
    setOptions([]);
    onSelect?.(val, option);
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let newValue = e.target.value;
    // 手动限制最大长度为 500
    if (newValue.length > 500) {
      newValue = newValue.slice(0, 500);
    }
    onChange?.(newValue);
    // 人工会话时禁用搜索功能
    if (imServiceChannel !== ImServiceChannel.Robot) {
      return;
    }

    if (!newValue.trim()) {
      setOptions([]);
    }
    handleSearch(newValue);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;
    // 手动限制最大长度为 500
    if (newValue.length > 500) {
      newValue = newValue.slice(0, 500);
    }
    onChange?.(newValue);
    // 人工会话时禁用搜索功能
    if (imServiceChannel !== ImServiceChannel.Robot) {
      return;
    }

    if (!newValue.trim()) {
      setOptions([]);
    }
    handleSearch(newValue);
  };

  const handleTextareaKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    // forward as input keydown
    onKeyDown?.(e as unknown as React.KeyboardEvent<HTMLInputElement>);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e);
  };

  // 使用稳定的 placeholder，避免 hydration 错误
  const stablePlaceholder = mounted ? placeholder || "" : "";

  // 自定义 textarea 作为 AutoComplete 的输入元素
  const customTextarea = (
    <textarea
      value={value}
      placeholder={stablePlaceholder}
      onChange={handleTextareaChange}
      onKeyDown={handleTextareaKeyDown}
      onPaste={onPaste}
      onFocus={onFocus}
      rows={1}
      className={className}
      style={{ width: "100%", boxSizing: "border-box" }}
      ref={setInputRefs as any}
      disabled={disabled}
    />
  );

  // 自定义 input 作为 AutoComplete 的输入元素
  const customInput = (
    <input
      type="text"
      value={value}
      placeholder={stablePlaceholder}
      onChange={handleInputChange}
      onKeyDown={handleInputKeyDown}
      onPaste={onPaste}
      onFocus={onFocus}
      className={className}
      style={{ width: "100%", boxSizing: "border-box" }}
      ref={setInputRefs as any}
      disabled={disabled}
    />
  );

  useLayoutEffect(() => {
    applyMaxLengthToElement(internalRef.current);
  }, [maxLength]);

  const mergedClassNames = classNames
    ? classNames
    : popupClassName
      ? { popup: { root: popupClassName } }
      : undefined;

  return (
    <div style={{ position: "relative" }} {...{ [POPUP_ROOT_ATTR]: "" }}>
      <AntdAutoComplete
        value={value}
        onSelect={handleSelect}
        popupMatchSelectWidth={true}
        style={{ width: "100%" }}
        getPopupContainer={getPopupContainer}
        // removeChild 多见于 rc-select 下拉 Portal + 动画与 React 提交不同步，并非只来自虚拟列表；
        // virtual=false 仍会走其它 DOM 路径（如无障碍 live region），故可能仍上报；配合关闭过渡减轻竞态。
        virtual={false}
        transitionName=""
        options={options.map((item) => ({
          value: item.id,
          label: (
            <span
              dangerouslySetInnerHTML={{ __html: item.topic }}
              style={{
                display: "block",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            />
          ),
          topic: item.topic, // 保存原始 topic 字符串供 onSelect 使用
        }))}
        classNames={mergedClassNames}
      >
        {inputType === "input" ? customInput : customTextarea}
      </AntdAutoComplete>
    </div>
  );
};

export default AutoComplete;
